
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useIsMobile } from "@/hooks/use-mobile";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Ship, BarChart, AlertCircle, CheckCircle2, FileText, ArrowRight, CreditCard, Wrench, DollarSign } from "lucide-react";
import EnhancedSeoMetadataComponent from "@/components/common/EnhancedSeoMetadata";
import { getEnhancedSeoDataForRoute } from "@/utils/enhancedSeoUtils";

const VenelainaPage = () => {
  const isMobile = useIsMobile();
  const seoData = getEnhancedSeoDataForRoute("/lainat/venelaina");
  
  // Fade in animation variant
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.5,
      }
    })
  };
  
  return (
    <div className="min-h-screen bg-white p-4 md:p-8">
      <EnhancedSeoMetadataComponent
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonicalUrl="https://lainasampo.fi/lainat/venelaina"
        schema={seoData.schema}
      />
      
      <div className="container mx-auto max-w-4xl">
        <motion.div
          initial="hidden"
          animate="visible"
          custom={0}
          variants={fadeIn}
        >
          <h1 className="text-3xl font-bold mb-6 text-blue-700">Venelaina</h1>
        </motion.div>
        
        <motion.div 
          className="relative bg-gradient-to-r from-blue-600 to-blue-700 p-8 rounded-lg mb-8 overflow-hidden text-white"
          initial="hidden"
          animate="visible" 
          custom={1}
          variants={fadeIn}
        >
          <div className="absolute right-0 top-0 opacity-10 md:opacity-20">
            <Ship className="w-48 h-48" />
          </div>
          <div className="relative z-10">
            <h2 className="text-2xl font-semibold mb-4">Rahoitus unelmiesi veneen hankintaan</h2>
            <p className="text-blue-100 mb-4">
              Venelaina on suunniteltu sekä uuden että käytetyn veneen hankintaan. Se on joustava 
              tapa rahoittaa venehankinta, kun et halua maksaa koko summaa kerralla.
            </p>
            <p className="text-blue-100">
              Lainasampo.fi:n kautta voit vertailla eri rahoitusyhtiöiden venelainoja ja löytää 
              edullisimman vaihtoehdon.
            </p>
          </div>
        </motion.div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <motion.div initial="hidden" animate="visible" custom={2} variants={fadeIn}>
            <Card className="h-full bg-white hover:shadow-md transition-shadow">
              <CardHeader>
                <div className="flex items-center">
                  <Ship className="h-5 w-5 mr-2 text-blue-600" />
                  <CardTitle className="text-blue-700">Venelainan vaihtoehdot</CardTitle>
                </div>
                <CardDescription>Erilaiset rahoitustavat veneelle</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 mr-2 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">
                      <span className="font-medium">Osamaksukauppa</span> - vene toimii lainan vakuutena
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 mr-2 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">
                      <span className="font-medium">Vakuudeton kulutusluotto</span> veneen ostoon
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 mr-2 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">
                      <span className="font-medium">Venetakaus</span> - mahdollisuus edullisempaan lainaan
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 mr-2 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">
                      <span className="font-medium">Veneliikkeen rahoitus</span> - usein joustavat ehdot
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 mr-2 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">
                      <span className="font-medium">Pankin venelaina</span> - yleensä edullinen korko
                    </span>
                  </li>
                </ul>
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <p className="text-gray-700 text-sm">
                    Vertaa tarkkaan eri rahoitusvaihtoehtoja. Korkoerot voivat tuoda jopa satojen eurojen 
                    säästöt lainan kokonaiskustannuksissa.
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
          
          <motion.div initial="hidden" animate="visible" custom={3} variants={fadeIn}>
            <Card className="h-full bg-white hover:shadow-md transition-shadow">
              <CardHeader>
                <div className="flex items-center">
                  <BarChart className="h-5 w-5 mr-2 text-blue-600" />
                  <CardTitle className="text-blue-700">Näin haet venelainaa</CardTitle>
                </div>
                <CardDescription>Veneen rahoittaminen on helppoa</CardDescription>
              </CardHeader>
              <CardContent>
                <ol className="space-y-3 list-decimal pl-5">
                  <li className="text-gray-700">
                    <span className="font-medium">Päätä budjetti ja rahoitustarve</span> veneen ostoon
                  </li>
                  <li className="text-gray-700">
                    <span className="font-medium">Täytä lainahakemus</span> Lainasampo.fi:ssä
                  </li>
                  <li className="text-gray-700">
                    <span className="font-medium">Vertaile eri rahoitusyhtiöiden</span> tarjouksia
                  </li>
                  <li className="text-gray-700">
                    <span className="font-medium">Valitse itsellesi sopivin</span> rahoitusvaihtoehto
                  </li>
                  <li className="text-gray-700">
                    <span className="font-medium">Allekirjoita sopimus</span> ja osta vene
                  </li>
                </ol>
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <p className="text-gray-700 text-sm">
                    Lainahakemuksen täyttäminen verkossa vie vain muutaman minuutin. Saat lainatarjoukset
                    nopeasti ja voit tehdä rahoituspäätöksen samana päivänä.
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
        
        <motion.div 
          initial="hidden" 
          animate="visible" 
          custom={4} 
          variants={fadeIn}
          className="mb-8"
        >
          <Card>
            <CardHeader>
              <div className="flex items-center">
                <AlertCircle className="h-5 w-5 mr-2 text-blue-600" />
                <CardTitle className="text-xl text-blue-700">Mitä tulee huomioida venelainaa hakiessa?</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="font-medium text-blue-700 mb-2">Kokonaiskustannukset</h3>
                <p className="text-gray-700">
                  Veneen ostaminen on huomattava investointi, mutta myös sen ylläpito aiheuttaa kuluja.
                  Venelainaa harkitessa on tärkeää huomioida myös muut kustannukset kuten vakuutukset,
                  verot, polttoainekulut ja huollot.
                </p>
              </div>
              
              <div>
                <h3 className="font-medium text-blue-700 mb-2">Vakuudet ja käsiraha</h3>
                <p className="text-gray-700">
                  Osamaksukaupassa vene toimii vakuutena, mutta useimmiten vaaditaan myös käsirahaa.
                  Yleensä käsirahan määrä on 10-30% veneen hinnasta. Käsirahan suuruus vaikuttaa
                  kuukausierän suuruuteen ja laina-aikaan.
                </p>
              </div>
              
              <div>
                <h3 className="font-medium text-blue-700 mb-2">Veneen käyttökustannukset</h3>
                <p className="text-gray-700">
                  Huomioi veneen omistamiseen liittyvät muut kulut: vakuutukset, venepaikka tai säilytyspaikka, 
                  polttoaine, huollot ja korjaukset. Näiden kulujen yhteissumma voi olla jopa
                  useita satoja euroja kuukaudessa.
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="flex items-start">
                    <CreditCard className="h-5 w-5 mr-2 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-blue-700 mb-1">Vakuutuskulut</p>
                      <p className="text-sm text-gray-700">
                        Venevakuutus on yleensä pakollinen osa venelainaa ja sen hinta vaihtelee veneen 
                        koon ja arvon mukaan.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="flex items-start">
                    <Wrench className="h-5 w-5 mr-2 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-blue-700 mb-1">Huoltokustannukset</p>
                      <p className="text-sm text-gray-700">
                        Varaa huoltoihin ja korjauksiin vuosittain 5-10% veneen arvosta riippuen veneen iästä ja käytöstä.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="flex items-start">
                    <DollarSign className="h-5 w-5 mr-2 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-blue-700 mb-1">Säilytys ja käyttö</p>
                      <p className="text-sm text-gray-700">
                        Venepaikan vuokra, talvisäilytys ja polttoainekulut voivat maksaa yhteensä 1000-3000€ vuodessa.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
        
        <motion.div
          initial="hidden"
          animate="visible"
          custom={5}
          variants={fadeIn}
          className="mb-8"
        >
          <Card>
            <CardHeader>
              <div className="flex items-center">
                <FileText className="h-5 w-5 mr-2 text-blue-600" />
                <CardTitle className="text-xl text-blue-700">Hyödyllisiä artikkeleita venerahoituksesta</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Link to="/artikkelit/venelainaopas-nain-rahoitat-unelmiesi-veneen" className="block bg-blue-50 p-4 rounded-lg hover:bg-blue-100 transition-colors">
                  <h3 className="font-medium text-blue-700 mb-2">Venelainaopas</h3>
                  <p className="text-sm text-gray-700 mb-2">
                    Kattava opas venelainan hakemiseen, eri vaihtoehtoihin ja siihen, mitä sinun 
                    kannattaa tietää ennen lainahakemuksen täyttämistä.
                  </p>
                  <p className="text-blue-600 text-sm font-medium">Lue lisää →</p>
                </Link>
                
                <Link to="/artikkelit/hauska-lomalaina-rahoita-unelmalomasi-suomen-parhaisiin-luontokohteisiin" className="block bg-blue-50 p-4 rounded-lg hover:bg-blue-100 transition-colors">
                  <h3 className="font-medium text-blue-700 mb-2">Veneilykohteet Suomessa</h3>
                  <p className="text-sm text-gray-700 mb-2">
                    Tutustu Suomen parhaisiin veneilykohteisiin ja saariston helmiin, jotka voit 
                    saavuttaa omalla veneellä.
                  </p>
                  <p className="text-blue-600 text-sm font-medium">Lue lisää →</p>
                </Link>
                
                <Link to="/artikkelit/yhdistelylaina-ratkaisu-useiden-pikavippien-kulutusluottojen-kanssa" className="block bg-blue-50 p-4 rounded-lg hover:bg-blue-100 transition-colors">
                  <h3 className="font-medium text-blue-700 mb-2">Kulutusluotot veneen rahoituksessa</h3>
                  <p className="text-sm text-gray-700 mb-2">
                    Milloin kulutusluotto on hyvä vaihtoehto veneen rahoittamiseen ja miten kulutusluottojen 
                    korkoja kannattaa vertailla.
                  </p>
                  <p className="text-blue-600 text-sm font-medium">Lue lisää →</p>
                </Link>
                
                <Link to="/artikkelit/vastuullinen-lainaaminen-nain-teet-kestavia-rahoituspaatoksia" className="block bg-blue-50 p-4 rounded-lg hover:bg-blue-100 transition-colors">
                  <h3 className="font-medium text-blue-700 mb-2">Vastuullinen venerahoitus</h3>
                  <p className="text-sm text-gray-700 mb-2">
                    Miten rahoittaa venehankinta vastuullisesti ja kestävästi. Vinkkejä taloudellisiin ja 
                    ekologisiin valintoihin veneilyssä.
                  </p>
                  <p className="text-blue-600 text-sm font-medium">Lue lisää →</p>
                </Link>
              </div>
            </CardContent>
          </Card>
        </motion.div>
        
        <motion.div
          initial="hidden"
          animate="visible"
          custom={6}
          variants={fadeIn}
          className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 rounded-lg"
        >
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="mb-4 md:mb-0">
              <h2 className="text-xl font-semibold mb-2">Kilpailuta venelainasi nyt!</h2>
              <p className="text-blue-100">
                Veneen rahoituksessa voit säästää satoja tai jopa tuhansia euroja kilpailuttamalla lainan. 
                Hae tarjouksia nyt!
              </p>
            </div>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Link
                to="/kilpailuta"
                className="inline-block bg-white text-blue-600 px-6 py-3 rounded-md font-medium hover:bg-blue-50 transition-colors"
              >
                Hae venelainaa <ArrowRight className="inline-block ml-1 h-4 w-4" />
              </Link>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default VenelainaPage;
